/**
 * Shelf labels (Stock 18 #10 "Scan & move"): a Code 128 barcode of the part number + the description and
 * where it sits (store · shelf). The phone scanner (components/ui/barcode-scanner) reads Code 128, and a USB
 * scanner simply types the part number into the Stock search. Printed from its own window, A4, 3 × 8 labels.
 * No library: Code 128 set B is a fixed table of bar widths.
 */
import { formatDate } from '@/lib/format';
import { binOf, isValidPartNumber, locationShort, stockLocations } from './inventoryModel';

// Bar/space widths of the 107 Code 128 symbols (value 0–105, 106 = stop).
const PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112'
];
const START_B = 104;
const STOP = 106;
const QUIET_MODULES = 10;

/** True when every character can be written in Code 128 set B (printable ASCII). */
export const canEncodeCode128 = (text) => /^[\x20-\x7E]+$/.test(String(text ?? ''));

/** The symbol values of `text` in set B, with the start code, the checksum and the stop code. */
export function code128Values(text) {
  const value = String(text ?? '');
  if (!canEncodeCode128(value)) throw new Error('Only printable ASCII can be put in a Code 128 barcode');
  const data = [...value].map((char) => char.charCodeAt(0) - 32);
  const checksum = data.reduce((sum, symbol, index) => sum + symbol * (index + 1), START_B) % 103;
  return [START_B, ...data, checksum, STOP];
}

/** Bar runs [{ x, width }] in modules (quiet zone included) and the total width in modules. */
export function code128Bars(text) {
  const bars = [];
  let x = QUIET_MODULES;
  code128Values(text).forEach((symbol) => {
    [...PATTERNS[symbol]].forEach((digit, index) => {
      const width = Number(digit);
      if (index % 2 === 0) bars.push({ x, width });
      x += width;
    });
  });
  return { bars, modules: x + QUIET_MODULES };
}

/** An SVG string of the barcode (black bars on white), `height` in px; the width follows the modules. */
export function code128Svg(text, { height = 46, moduleWidth = 1.4 } = {}) {
  const { bars, modules } = code128Bars(text);
  const width = Math.round(modules * moduleWidth * 100) / 100;
  const rects = bars.map((bar) => `<rect x="${bar.x}" y="0" width="${bar.width}" height="${height}"/>`).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${modules} ${height}" preserveAspectRatio="none" shape-rendering="crispEdges" role="img" aria-label="Barcode ${escapeHtml(text)}"><rect width="${modules}" height="${height}" fill="#fff"/><g fill="#000">${rects}</g></svg>`;
}

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

/**
 * One label per item and store it sits in (or one with no store when it is in none): part number, barcode,
 * description, "AD Store · Shelf B3". Items whose part number cannot be a barcode are listed in `skipped`.
 */
export function labelEntries(items = []) {
  const entries = [];
  const skipped = [];
  items.forEach((item) => {
    const partNumber = String(item?.part_number ?? '').trim();
    if (!partNumber || !canEncodeCode128(partNumber) || !isValidPartNumber(partNumber)) {
      skipped.push(partNumber || '(blank)');
      return;
    }
    const where = stockLocations(item);
    const stores = where.length ? where.map((entry) => entry.location) : [''];
    stores.forEach((location) => entries.push({
      partNumber,
      description: String(item.description || '').trim(),
      location,
      bin: location ? binOf(item, location) : ''
    }));
  });
  return { entries, skipped };
}

export function labelsHtml(entries = []) {
  const labels = entries.map((entry) => `
    <div class="label">
      <div class="pn">${escapeHtml(entry.partNumber)}</div>
      <div class="desc">${escapeHtml(entry.description || '—')}</div>
      <div class="bc">${code128Svg(entry.partNumber)}</div>
      <div class="where">${escapeHtml([entry.location ? locationShort(entry.location) : '', entry.bin].filter(Boolean).join(' · ') || 'Scan to open in Sara')}</div>
    </div>`).join('');
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>Shelf labels · ${escapeHtml(formatDate(new Date().toISOString()))}</title>
<style>
  * { box-sizing: border-box; }
  /* A4 (210 × 297 mm) − 8 mm margins = 194 × 281 mm printable. 3 × 63 + 2 × 1.5 = 192 mm wide and
     8 × 33.5 + 7 × 1.5 = 278.5 mm tall: 24 labels fill exactly one page, with room for printer rounding. */
  @page { size: A4; margin: 8mm; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #111; }
  .sheet { display: grid; grid-template-columns: repeat(3, 63mm); grid-auto-rows: 33.5mm; gap: 1.5mm; }
  .label { border: 1px dashed #c8cad6; border-radius: 2mm; padding: 2mm 3mm; display: flex; flex-direction: column; overflow: hidden; break-inside: avoid; }
  .pn { font-family: 'Courier New', monospace; font-weight: 700; font-size: 13pt; }
  .desc { font-size: 7.5pt; color: #444; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .bc { flex: 1; display: flex; align-items: center; }
  .bc svg { max-width: 100%; height: 11mm; }
  .where { font-size: 8pt; font-weight: 700; }
  @media screen { body { padding: 12px; background: #f3f4f9; } .label { background: #fff; } }
</style></head>
<body><div class="sheet">${labels}</div></body></html>`;
}

/** Opens the labels in their own window and prints them. Returns false when pop-ups are blocked. */
export function printShelfLabels(entries) {
  const win = typeof window !== 'undefined' ? window.open('', '_blank', 'width=900,height=1100') : null;
  if (!win) return false;
  win.document.open();
  win.document.write(labelsHtml(entries));
  win.document.close();
  win.focus();
  setTimeout(() => {
    try { win.print(); } catch (err) { /* the user can still print from the window */ }
  }, 250);
  return true;
}
